import React, { useState } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../utils/config";
import {
  FaCalendarAlt, FaClock, FaCheck, FaTimes, FaInbox,
  FaSearch, FaVideo, FaMapMarkerAlt, FaBriefcase, FaInfoCircle
} from "react-icons/fa";

const Interview = () => {
  const { appliedJobs, searchQuery: globalSearchQuery, fetchDashboardData } = useOutletContext();

  const [localSearchQuery, setLocalSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [confirmingId, setConfirmingId] = useState(null);

  const interviewApps = appliedJobs.filter(app =>
    app.interviewStatus === "Invited" || app.interviewStatus === "Rescheduled"
  );

  const statuses = ["Pending", "Confirmed", "Declined"];

  const filteredApps = interviewApps.filter(app => {
    const combinedQuery = (globalSearchQuery || localSearchQuery).toLowerCase();
    const matchesSearch =
      (app.jobId?.role || app.role || "").toLowerCase().includes(combinedQuery) ||
      (app.candidateInterviewStatus || "").toLowerCase().includes(combinedQuery);

    const matchesStatus = statusFilter
      ? (app.candidateInterviewStatus || "Pending") === statusFilter
      : true;

    let matchesDate = true;
    if (dateFilter && app.interviewTime) {
      const schedDate = new Date(app.interviewTime).toISOString().split("T")[0];
      matchesDate = schedDate === dateFilter;
    }

    return matchesSearch && matchesStatus && matchesDate;
  });

  const handleConfirm = async (applicationId, status, note = "") => {
    setConfirmingId(applicationId + status);
    try {
      const token = localStorage.getItem("candidateToken");
      await axios.put(
        `${API_BASE_URL}/candidate/confirm-interview`,
        { applicationId, status, note },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchDashboardData();
    } catch (err) {
      console.error("Error updating interview status:", err);
      alert("Failed to update status. Please try again.");
    } finally {
      setConfirmingId(null);
    }
  };

  const resetFilters = () => {
    setLocalSearchQuery("");
    setStatusFilter("");
    setDateFilter("");
  };

  return (
    <div className="w-full h-full">
      
      {/* ── Header & Filters ── */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-8">
        <div className="flex flex-wrap items-center gap-4">
          
          <div className="flex items-center gap-4 mr-auto">
             <div className="p-3 bg-gradient-to-br from-green-50 to-blue-50 rounded-xl text-blue-600 shadow-sm border border-blue-100/50">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-green-500 to-blue-600 flex items-center justify-center text-white shadow-md">
                   <FaVideo size={20} />
                </div>
             </div>
             <div>
                <h1 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-green-500 to-blue-600">INTERVIEW SCHEDULE</h1>
                <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">Manage your calendar</div>
             </div>
          </div>

          {/* Search */}
          <div className="relative flex-1 min-w-[150px]">
            <FaSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
            <input
              type="text"
              placeholder="Search interviews..."
              value={localSearchQuery}
              onChange={(e) => setLocalSearchQuery(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-xs font-bold border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none uppercase tracking-widest"
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-36 py-1.5 px-3 text-[10px] font-bold border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none uppercase tracking-widest bg-white"
          >
            <option value="">ALL STATUS</option>
            {statuses.map(s => <option key={s} value={s}>{s}</option>)}
          </select>

          {/* Date Filter */}
          <input
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="w-32 py-1.5 px-3 text-[10px] font-bold border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none uppercase tracking-widest bg-white"
          />

          {/* Reset */}
          {(localSearchQuery || statusFilter || dateFilter) && (
            <button
              onClick={resetFilters}
              className="h-7 px-3 text-[10px] font-bold text-gray-600 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition uppercase tracking-widest"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* ── Table Area ── */}
      <div className="p-0 bg-white border shadow-lg rounded-xl overflow-hidden">
        {filteredApps.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="text-xs text-left text-white bg-gradient-to-r from-green-500 to-blue-600">
                <tr>
                  <th className="py-3 px-4 text-center font-bold">Evaluation Target</th>
                  <th className="py-3 px-4 text-center font-bold">Temporal Coordinates</th>
                  <th className="py-3 px-4 text-center font-bold">Protocol</th>
                  <th className="py-3 px-4 text-center font-bold">Gateway Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredApps.map((app) => (
                  <tr key={app._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-2 text-center">
                      <div className="text-xs font-bold text-gray-800">{app.jobId?.role || "Position Data"}</div>
                      <div className="text-[9px] text-gray-400 font-bold uppercase tracking-wider mt-0.5">Ref: {app._id.slice(-6).toUpperCase()}</div>
                    </td>
                    <td className="px-4 py-2 text-center">
                      <div className="inline-flex items-center justify-center gap-1.5 px-3 py-1 bg-gray-50 rounded-lg border border-gray-100 text-[10px] font-bold text-gray-600">
                        <FaClock className="text-blue-500" />
                        {new Date(app.interviewTime).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-center">
                      <div className="flex flex-col items-center justify-center gap-1">
                        <span className={`text-[9px] font-bold px-2 py-0.5 rounded uppercase tracking-wider flex items-center gap-1 ${app.interviewMode === "online" ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"}`}>
                           {app.interviewMode === "online" ? <FaVideo className="text-[8px]"/> : <FaMapMarkerAlt className="text-[8px]"/>}
                          {app.interviewMode}
                        </span>
                        {app.interviewMode === "online" && app.interviewMeetLink && (
                          <a
                            href={app.interviewMeetLink}
                            target="_blank"
                            rel="noreferrer"
                            className="text-[9px] font-bold text-blue-500 hover:text-blue-700 underline uppercase mt-0.5 block tracking-widest"
                          >
                            Join Uplink
                          </a>
                        )}
                        {app.interviewMode === "offline" && app.interviewAddress && (
                          <span className="text-[8px] font-bold text-gray-400 uppercase tracking-widest max-w-[120px] truncate block mt-0.5" title={app.interviewAddress}>
                            {app.interviewAddress}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-center">
                      <div className="flex flex-col items-center justify-center gap-1">
                        {(!app.candidateInterviewStatus || app.candidateInterviewStatus === "Pending") ? (
                          <div className="flex gap-4">
                            <button
                              onClick={() => handleConfirm(app._id, "Confirmed")}
                              disabled={!!confirmingId}
                              className="text-emerald-600 hover:text-emerald-800 transition-all active:scale-90 disabled:opacity-30"
                              title="Accept"
                            >
                              <FaCheck size={18} />
                            </button>
                            <button
                              onClick={() => {
                                const reason = prompt("Optional: Provide a reason or request an alternate time:");
                                if (reason !== null) handleConfirm(app._id, "Declined", reason);
                              }}
                              disabled={!!confirmingId}
                              className="text-rose-600 hover:text-rose-800 transition-all active:scale-90 disabled:opacity-30"
                              title="Decline/Reschedule"
                            >
                              <FaTimes size={18} />
                            </button>
                          </div>
                        ) : (
                          <span className={`text-[9px] px-2 py-0.5 font-bold uppercase tracking-widest rounded ${app.candidateInterviewStatus === "Confirmed" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                            {app.candidateInterviewStatus}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-12 text-center">
            <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-3 text-gray-300">
              <FaVideo size={20} />
            </div>
            <h3 className="text-sm font-bold text-gray-700 mb-1">No Interviews Scheduled</h3>
            <p className="text-[10px] text-gray-400 font-medium tracking-widest uppercase">When you are invited for an interview, it will appear here.</p>
          </div>
        )}
      </div>

      <div className="flex items-center justify-center gap-2 text-[9px] font-bold text-gray-400 uppercase tracking-widest pt-6 pb-2">
        <FaInfoCircle className="text-blue-400" />
        Timely Health Interview Scheduler • {new Date().getFullYear()}
      </div>
    </div>
  );
};

export default Interview;